import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { Storage } from '@angular/fire/storage';
import {
  NgCommonModule,
  NgFireMediaUploader,
} from '@principle-theorem/ng-shared';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '@principle-theorem/ng-auth';
import { EditorPresetsService } from '@principle-theorem/ng-interactions';
import {
  ContentContainerComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import {
  Referral,
  ReferralPortalLink,
} from '@principle-theorem/principle-core';
import { IReferral } from '@principle-theorem/principle-core/interfaces';
import { filterUndefined, WithRef } from '@principle-theorem/shared';
import { combineLatest, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ReferralAttachmentMenuComponent } from '../../components/referral-attachment-menu/referral-attachment-menu.component';
import { ReferralCommunicationComponent } from '../../components/referral-communication/referral-communication.component';
import { ReferralImageUploadComponent } from '../../components/referral-image-upload/referral-image-upload.component';
import { ReferralMediaComponent } from '../../components/referral-media/referral-media.component';
import { ReferralParticipantDetailsComponent } from '../../components/referral-participant-details/referral-participant-details.component';
import { ReferralPatientDetailsComponent } from '../../components/referral-patient-details/referral-patient-details.component';
import { ReferralPortalAccessIssueComponent } from '../../components/referral-portal-access-issue/referral-portal-access-issue.component';
import { ReferralToolbarComponent } from '../../components/referral-toolbar/referral-toolbar.component';
import { ReferralService } from '../../services/referral.service';
import { ReferralStore } from '../../stores/referral.store';

@Component({
  selector: 'pr-referral-portal',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTabsModule,
    MatTooltipModule,
    RouterModule,
    ContentContainerComponent,
    ReferralCommunicationComponent,
    ReferralImageUploadComponent,
    ReferralMediaComponent,
    ReferralParticipantDetailsComponent,
    ReferralPatientDetailsComponent,
    ReferralPortalAccessIssueComponent,
    ReferralToolbarComponent,
    ReferralAttachmentMenuComponent,
  ],
  templateUrl: './referral-portal.component.html',
  styleUrl: './referral-portal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReferralStore],
})
export class ReferralPortalComponent {
  private _authService = inject(AuthService);
  private _editorPresets = inject(EditorPresetsService);
  private _organisation = inject(OrganisationService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _storage = inject(Storage);
  private _referralUid$ = new ReplaySubject<string>(1);
  public referralService = inject(ReferralService);
  public store = inject(ReferralStore);
  menuItems = signal<MenuButtonLoaderFn[]>([]);
  previewOnly = input(false, { transform: coerceBooleanProperty });
  previewReferral = input<WithRef<IReferral> | undefined>(undefined);
  readonly signedInEmail = toSignal(
    this._authService.authUser$.pipe(map((user) => user?.email ?? undefined))
  );
  readonly expectedEmail = toSignal(
    this._route.queryParamMap.pipe(
      map(
        (params) => params.get(ReferralPortalLink.loginHintParam) ?? undefined
      )
    )
  );

  constructor() {
    this._route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      if (params['referralUid']) {
        this._referralUid$.next(params['referralUid'] as string);
      }
    });

    combineLatest([
      this._authService.authUser$.pipe(filterUndefined()),
      this._referralUid$.pipe(filterUndefined()),
    ])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([user, uid]) => {
        if (!user.email) {
          this.store.markReferralUnavailable();
          return;
        }
        this.store.loadReferralForPortal({ email: user.email, uid });
      });

    this.store.referral$
      .pipe(filterUndefined(), take(1), takeUntilDestroyed())
      .subscribe((referral) => {
        this.store.loadMedia(referral);
        this.store.loadClinicalNotes(referral);
        const contact = Referral.getContact(referral);
        if (!contact) {
          return;
        }
        this.store.setCurrentUser(contact);
        this.store.loadMessages(referral);
        void this.referralService.markAsRead(referral, contact);
      });

    const uploader = new NgFireMediaUploader(
      this._storage,
      this._organisation.storagePath$.pipe(filterUndefined())
    );
    this.menuItems.set(this._editorPresets.defaultMenuItems(uploader));

    toObservable(this.previewOnly)
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((isPreviewOnly) => {
        if (!isPreviewOnly) {
          this.store.setupAutoSave();
        }
      });

    combineLatest([
      toObservable(this.previewReferral).pipe(filterUndefined()),
      toObservable(this.previewOnly),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([referral, isPreviewOnly]) => {
        if (isPreviewOnly) {
          this.store.setReferral(referral);
        }
      });
  }

  async switchAccount(): Promise<void> {
    const redirectTo = this._router.url;
    await this._authService.logout();
    await this._router.navigate(['/login'], { queryParams: { redirectTo } });
  }
}
